import { inject, Injectable, Signal } from '@angular/core';
import { ComponentStore } from '@ngrx/component-store';
import { AssetStateMessage, UsersIamDivisionChoice } from '@r1-compass-library/models';
import { GlobalLoggedInUserStore } from '@r1-compass-library/store/logged-in-user/global-logged-in-user.store';
import { Observable, Subscription } from 'rxjs';
import { SwitchDivisionService } from '@r1-compass-library/services/switch-division.service';

@Injectable()
export abstract class AssetStore<
    ASSET extends { iamDivisionId: number },
    STATE extends object,
> extends ComponentStore<STATE> {
    readonly #loggedInUserStore: GlobalLoggedInUserStore = inject(GlobalLoggedInUserStore);
    readonly #switchDivisionService = inject(SwitchDivisionService);

    usersAvailableDivisions: Signal<UsersIamDivisionChoice[]> = this.#loggedInUserStore.usersAvailableDivisions;

    abstract assetTypeName: string;

    protected constructor(state: STATE) {
        super(state);
    }

    handleSearchResponse(
        assets: ASSET[],
        entityUpdater: (observableOrValue: ASSET | Observable<ASSET>) => Subscription,
        messageUpdater: (observableOrValue: AssetStateMessage | Observable<AssetStateMessage>) => Subscription
    ) {
        if (assets?.length > 0) {
            const retrievedAsset = assets[0];
            if (retrievedAsset.iamDivisionId !== this.#loggedInUserStore.selectedIamDivision()?.id) {
                const divisionCode =
                    this.usersAvailableDivisions().find(division => division.id === retrievedAsset.iamDivisionId)
                        ?.divisionCode ?? 'Unknown';
                this.#switchDivisionService.switchDivision(retrievedAsset.iamDivisionId, divisionCode).subscribe(() => {
                    messageUpdater({
                        type: 'Info',
                        message: `Switched to ${divisionCode} division.`,
                    });
                    entityUpdater(retrievedAsset);
                });
            } else {
                entityUpdater(retrievedAsset);
            }
            return;
        } else {
            // no match found for any division, they don't have access/it doesn't exist
            messageUpdater({
                type: 'Error',
                message: `No ${this.assetTypeName} found or user doesn't have access.`,
            });
            return;
        }
    }
}
